import React from 'react';
import PropTypes from 'prop-types';
import NeoButton from './neoButton';
import Container from 'react-bootstrap/Container';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import NeoModal from './neoModal';
import HTMLContent from './htmlContent';
import Message from '../components/resale/seller/components/message';
import '../style/neo-layout.css';
import { ReactComponent as Validate } from "../picture/icon-validate.svg";
import { ReactComponent as Donation } from "../picture/donnation.svg";
import PictureStyle from '../style/components/neoPicture.module.css';
import { logout } from '../utils/logout';


const MAPPING_ICON = {
    "validate": Validate,
    "donation": Donation
}

export function NeoLayoutHeader({ config: { title, description }, step }) {
    if (!title) {
        return null;
    }

    let { header_icon } = (step?.data || {});

    let Component = 'h2';
    let _title = title;
    if (title instanceof Object && title.type) {
        Component = title.type;
        _title = title.text;
        header_icon = title.icon || header_icon;
    }

    return (
        <div className={`wz-neo-layout-header ${header_icon ? PictureStyle['picture-container-title'] : ''}`}>
            {header_icon ? <NeoPicture className={`${PictureStyle['picture-container']} ${PictureStyle[Component]} ${PictureStyle['header-picture']}`} icon={header_icon} /> : null}
            <Component><HTMLContent content={_title} /></Component>
            {description && <h3>
                <HTMLContent content={description} />
            </h3>}
        </div>
    );
}

NeoLayoutHeader.propTypes = {
    config: PropTypes.object.isRequired
};

export function NeoLayoutContent({ children }) {
    if (React.Children.count(children) === 0) {
        return null;
    }

    return (
        <div className="wz-content wz-neo-layout-content">
            {children}
        </div>
    );
}

NeoLayoutContent.propTypes = {
    children: PropTypes.node.isRequired,
};

export function ConfirmationModal({ config: { title, description, button_ok, button_nok }, onSubmit, onNok, submitting = false }) {
    return (
        <NeoLayout.Content>
            <NeoLayout.Subtitle
                config={{ subtitle: title }}
            />
            <NeoDescription
                config={{ description }}
            />
            <NeoButton
                type="next"
                label={button_ok}
                disabled={submitting}
                onClick={onSubmit}
            />
            <NeoButton
                type="reset"
                label={button_nok}
                onClick={onNok}
            />
        </NeoLayout.Content>
    );
}

export function NeoLayoutFooter({ step, config = {}, onPreviousStep, onSubmit, submitDisabled = false, input, onSkip, widgetKey, storage = null }) {
    // TODO: set all this modal logic into custom hook or something
    const [modal, setModal] = React.useState(false);
    const [submitting, setSubmitting] = React.useState(false);

    const submitPromise = React.useCallback(event => {
        setSubmitting(true);
        let p = onSubmit(event);
        if (p instanceof Promise) {
            return p.finally(() => setSubmitting(false));
        } else {
            setSubmitting(false);
        }
    }, [onSubmit]);

    const submit = React.useCallback((event) => {
        if (config.modal) {
            event.preventDefault();
            return setModal(true);
        }
        submitPromise(event);
    }, [config, submitPromise]);

    const submitModal = React.useCallback((event) => {
        setModal(false);
        submitPromise({ target: 'modal', preventDefault: () => { } });
    }, [submitPromise]);

    const onDisconnect = React.useCallback((args) => {
        const contextLogoutMapping = {
            'resale-link-error' : 'resaleLinkError',
            'confirmation-check-resale-link-step': 'cancelCheckResaleLink',
            'waiting-list-ticket-link-error': 'waitingListTicketLinkError',
          }

        const contextLogout = contextLogoutMapping[step.slug] || '';
        return logout(contextLogout, storage);
    }, [step, storage])

    return (
        <React.Fragment>
            {modal && <NeoModal onHide={() => { }}>
                <ConfirmationModal
                    config={config.modal}
                    onSubmit={submitModal}
                    submitting={submitting}
                    onNok={() => setModal(false)}
                />
            </NeoModal>}
            <div className="wz-content wz-neo-layout-footer">
                <Row className="text-center">
                    <Col>
                        {!step.is_final && <NeoButton type="next" disabled={submitDisabled || submitting} label={config.label} onClick={submit} />}
                        {step.previous_allowed && <NeoButton type="previous" onClick={() => {
                            onSubmit({
                                data: {
                                    action: 'previous',
                                }
                            })
                        }
                        } />}
                        {step.skip_allowed && <NeoButton type="skip" onClick={submit} />}
                        {step.reset_allowed && <NeoButton type="reset" label={config.reset_label} onClick={onDisconnect} />}
                    </Col>
                </Row>
            </div>
        </React.Fragment>
    );
}

NeoLayoutFooter.propTypes = {
    step: PropTypes.object.isRequired,
    nextDisabled: PropTypes.bool,
    onPreviousStep: PropTypes.func,
    onSubmit: PropTypes.func,
    widgetKey: PropTypes.string,
    storage: PropTypes.object.isRequired
};

NeoLayoutFooter.defaultProps = {
    nextDisabled: false,
    onPreviousStep: () => { },
    onSubmit: () => { },
    widgetKey: null
};


export function NeoDescription({ config: { description } }) {
    if (!description) {
        return null;
    }

    return <HTMLContent className="wz-neo-layout-description" content={description} asContainer='p' />;
}

export function NeoPicture({ icon, className }) {
    if (/^http/.test(icon)) {
        return (
            <div className={className}><img className={PictureStyle['picture']} src={icon} alt={icon} /></div>
        )
    }
    let Component = MAPPING_ICON[icon];

    return (
        <div className={className}><Component className={PictureStyle['picture']} /></div>
    )
}

export function NeoSubtitle({ config, step }) {
    let { subtitle } = config;
    let { subtitle_icon } = (step?.data || {});

    if (!subtitle) {
        return null;
    }

    return (
        <div className={`${subtitle_icon ? PictureStyle['picture-container-title'] : ""} ${subtitle_icon ? PictureStyle['picture-container-subtitle'] : ""}`}>
            {subtitle_icon ? <NeoPicture className={`${PictureStyle['picture-container']} ${PictureStyle['subtitle-picture']}`} icon={subtitle_icon} /> : null}
            <HTMLContent className="wz-neo-layout-subtitle" content={subtitle} asContainer='p'/>
        </div>
    );
}

export function NeoMessage({ config }) {
    let { message } = config;

    if (!message) {
        return null;
    }

    return (
        <div className='wz-neo-layout-message'>
            <Message title={message.title} description={message.description} type='info'/>
        </div>
    )
}

export default function NeoLayout({ children, displayPowered, className = '' }) {
    return (
        <Container as='main' className={`${className} wz-neo-layout`}>
            {children}
        </Container>
    );
}

NeoLayout.defaultProps = {
    displayPowered: true
};

NeoLayout.propTypes = {
    displayPowered: PropTypes.bool,
    children: PropTypes.node.isRequired,
};

NeoLayout.Footer = NeoLayoutFooter;
NeoLayout.Header = NeoLayoutHeader;
NeoLayout.Content = NeoLayoutContent;
NeoLayout.Description = NeoDescription;
NeoLayout.Subtitle = NeoSubtitle;
NeoLayout.Message = NeoMessage;
